import { createElement, type ReactNode } from "react";

import { Platform, Text } from "react-native";

export type LineIconName =
  | "appointment"
  | "archive"
  | "bot"
  | "camera"
  | "case"
  | "chart"
  | "check"
  | "chevron-left"
  | "chevron-right"
  | "clock"
  | "clinician"
  | "document"
  | "edit"
  | "flag"
  | "hash"
  | "home"
  | "hospital"
  | "eye"
  | "image"
  | "info"
  | "lab"
  | "mail"
  | "medication"
  | "message"
  | "microphone"
  | "phone"
  | "plug"
  | "procedure"
  | "program"
  | "pulse"
  | "plus"
  | "search"
  | "settings"
  | "star"
  | "sync"
  | "stop"
  | "thumb-up"
  | "user"
  | "volume"
  | "volume-off"
  | "x";

const fallbackIconText: Record<LineIconName, string> = {
  appointment: "APT",
  archive: "ARC",
  bot: "AI",
  camera: "CAM",
  case: "CASE",
  chart: "CHT",
  check: "OK",
  "chevron-left": "<",
  "chevron-right": ">",
  clock: "24",
  clinician: "CLN",
  document: "DOC",
  edit: "ED",
  flag: "FLAG",
  eye: "VIEW",
  hash: "#",
  home: "HOME",
  hospital: "HSP",
  image: "IMG",
  info: "i",
  lab: "LAB",
  mail: "MAIL",
  medication: "RX",
  message: "MSG",
  microphone: "MIC",
  phone: "CALL",
  plug: "PLUG",
  procedure: "PROC",
  program: "PRG",
  pulse: "OP",
  plus: "+",
  search: "SEARCH",
  settings: "SET",
  star: "STAR",
  sync: "SYNC",
  stop: "STOP",
  "thumb-up": "UP",
  user: "PT",
  volume: "VOL",
  "volume-off": "MUTE",
  x: "X",
};

type LineIconProps = {
  color: string;
  name: LineIconName;
  size?: number;
  strokeWidth?: number;
};

export function LineIcon({
  color,
  name,
  size = 22,
  strokeWidth = 2.15,
}: LineIconProps): ReactNode {
  if (Platform.OS !== "web") {
    return (
      <Text
        style={{
          color,
          fontSize: Math.max(9, Math.floor(size * 0.42)),
          fontWeight: "800",
          lineHeight: size,
        }}
      >
        {fallbackIconText[name]}
      </Text>
    );
  }

  const iconProps = {
    fill: "none",
    stroke: color,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth,
  };
  const line = (
    key: string,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
  ) => createElement("line", { ...iconProps, key, x1, x2, y1, y2 });
  const path = (key: string, d: string) =>
    createElement("path", { ...iconProps, d, key });
  const circle = (key: string, cx: number, cy: number, r: number) =>
    createElement("circle", { ...iconProps, cx, cy, key, r });
  const rect = (
    key: string,
    x: number,
    y: number,
    width: number,
    height: number,
    rx = 2,
  ) => createElement("rect", { ...iconProps, height, key, rx, width, x, y });

  const childrenByName: Record<LineIconName, ReactNode[]> = {
    appointment: [
      rect("body", 4, 5, 16, 15, 2),
      line("top", 4, 10, 20, 10),
      line("left-bind", 8, 3, 8, 7),
      line("right-bind", 16, 3, 16, 7),
      line("mark-a", 8, 14, 8.1, 14),
      line("mark-b", 12, 14, 12.1, 14),
      line("mark-c", 16, 14, 16.1, 14),
    ],
    archive: [
      rect("box", 4, 7, 16, 13, 2),
      path("lid", "M3 5h18v4H3z"),
      line("handle", 9, 12, 15, 12),
    ],
    bot: [
      rect("face", 5, 7, 14, 11, 3),
      line("antenna", 12, 7, 12, 4),
      circle("tip", 12, 3.5, 0.8),
      circle("left-eye", 9, 12, 1),
      circle("right-eye", 15, 12, 1),
      path("mouth", "M9 15h6"),
    ],
    camera: [
      path("body", "M4 7h4l1.5-2h5L16 7h4v12H4z"),
      circle("lens", 12, 13, 3.2),
    ],
    case: [
      rect("body", 5, 6, 14, 15, 2),
      path("clip", "M9 6V4h6v2"),
      line("row-a", 8, 12, 16, 12),
      line("row-b", 8, 16, 14, 16),
    ],
    chart: [
      line("axis-y", 5, 19, 5, 5),
      line("axis-x", 5, 19, 20, 19),
      path("trend", "M8 15l3-4 3 2 4-6"),
      circle("point-a", 8, 15, 0.8),
      circle("point-b", 11, 11, 0.8),
      circle("point-c", 14, 13, 0.8),
      circle("point-d", 18, 7, 0.8),
    ],
    check: [path("mark", "M5 12l4 4L19 6")],
    "chevron-left": [path("chevron-left", "M15 18l-6-6 6-6")],
    "chevron-right": [path("chevron-right", "M9 18l6-6-6-6")],
    clock: [
      circle("face", 12, 12, 8),
      path("hands", "M12 7v5l3 2"),
    ],
    clinician: [
      circle("head", 12, 7, 3),
      path("body", "M6 20c1.2-4 10.8-4 12 0"),
      path("scope", "M7 10v2a5 5 0 0 0 10 0v-2"),
    ],
    document: [
      path("page", "M7 3h7l4 4v14H7z"),
      path("fold", "M14 3v5h5"),
      line("row-a", 9, 13, 15, 13),
      line("row-b", 9, 17, 15, 17),
    ],
    edit: [
      path("pencil", "M4 17l1 3 3-1L18.5 8.5 15.5 5.5 5 16z"),
      path("cap", "M14 7l3 3"),
    ],
    flag: [
      path("pole", "M6 20V5"),
      path("banner", "M6 5h11l-2.5 4L17 13H6"),
    ],
    eye: [
      path("eye", "M3 12s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6z"),
      circle("pupil", 12, 12, 2.4),
    ],
    hash: [
      path("left", "M9 4L7 20"),
      path("right", "M17 4l-2 16"),
      line("top", 4, 9, 20, 9),
      line("bottom", 3, 15, 19, 15),
    ],
    home: [
      path("roof", "M4 11l8-7 8 7"),
      path("house", "M6 10v10h12V10"),
      path("door", "M10 20v-6h4v6"),
    ],
    hospital: [
      rect("building", 5, 4, 14, 17, 2),
      path("cross-v", "M12 8v6"),
      path("cross-h", "M9 11h6"),
      path("base", "M9 21v-4h6v4"),
    ],
    image: [
      rect("frame", 4, 5, 16, 14, 2),
      path("mountain", "M7 16l3-3 2 2 3-4 2 5"),
      circle("sun", 9, 9, 1.2),
    ],
    info: [
      circle("face", 12, 12, 9),
      line("stem", 12, 11, 12, 17),
      line("dot", 12, 7, 12.01, 7),
    ],
    lab: [
      path("tube", "M9 3h6"),
      path("glass", "M10 3v5l-5 9a3 3 0 0 0 2.6 4.5h8.8A3 3 0 0 0 19 17l-5-9V3"),
      path("liquid", "M8 15h8"),
      circle("bubble-a", 10, 18, 0.7),
      circle("bubble-b", 14, 18, 0.7),
    ],
    mail: [
      rect("envelope", 3, 6, 18, 13, 2),
      path("flap", "M3.5 7.5l8.5 6 8.5-6"),
    ],
    medication: [
      path("pill", "M10 21a5 5 0 0 1-7-7l7-7a5 5 0 0 1 7 7z"),
      path("split", "M7 10l7 7"),
    ],
    message: [
      path("bubble", "M4 5h16v11H9l-5 4z"),
      line("row-a", 8, 9, 16, 9),
      line("row-b", 8, 13, 14, 13),
    ],
    microphone: [
      rect("mic", 9, 3, 6, 11, 3),
      path("stand-a", "M5 10a7 7 0 0 0 14 0"),
      line("stand-b", 12, 17, 12, 21),
      line("base", 8, 21, 16, 21),
    ],
    phone: [
      path(
        "handset",
        "M6.5 4.5l3 3-2 2c1.2 2.4 3.1 4.3 5.5 5.5l2-2 3 3-1.5 3c-.5 1-1.5 1.4-2.6 1.1C8.9 19 5 15.1 3.4 10.1c-.3-1.1.1-2.1 1.1-2.6z",
      ),
    ],
    plug: [
      path("body", "M9 7v5a3 3 0 0 0 6 0V7"),
      line("pin-a", 10, 3, 10, 7),
      line("pin-b", 14, 3, 14, 7),
      path("cord", "M12 15v3a3 3 0 0 0 3 3h1"),
    ],
    procedure: [
      path("tool-a", "M14.7 5.3a4 4 0 0 0 4 4L10 18l-4 1 1-4z"),
      path("tool-b", "M5 5l4 4"),
      line("handle", 3, 7, 7, 3),
    ],
    program: [path("wave", "M4 12h4l2-5 4 10 2-5h4")],
    pulse: [path("activity", "M3 12h4l2-5 4 10 2-5h6")],
    plus: [line("vertical", 12, 5, 12, 19), line("horizontal", 5, 12, 19, 12)],
    search: [
      circle("lens", 10.5, 10.5, 6),
      line("handle", 15, 15, 20, 20),
    ],
    settings: [
      circle("center", 12, 12, 3),
      path("gear", "M12 3v3M12 18v3M4.2 7.5l2.6 1.5M17.2 15l2.6 1.5M4.2 16.5L6.8 15M17.2 9l2.6-1.5"),
      path("ring", "M8.5 5.8l-1.7 1 1 1.7a5.7 5.7 0 0 0 0 7l-1 1.7 1.7 1 1-1.7a5.7 5.7 0 0 0 7 0l1 1.7 1.7-1-1-1.7a5.7 5.7 0 0 0 0-7l1-1.7-1.7-1-1 1.7a5.7 5.7 0 0 0-7 0z"),
    ],
    star: [
      path(
        "star",
        "M12 3l2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.2l-5.6 3 1.1-6.2L3 9.6l6.2-.9z",
      ),
    ],
    sync: [
      path("top", "M19 7v5h-5"),
      path("top-curve", "M18 12a6 6 0 0 0-10-4.5L5 10"),
      path("bottom", "M5 17v-5h5"),
      path("bottom-curve", "M6 12a6 6 0 0 0 10 4.5l3-2.5"),
    ],
    stop: [rect("stop", 7, 7, 10, 10, 2)],
    "thumb-up": [
      path("wrist", "M7 10v10H4a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2z"),
      path(
        "hand",
        "M7 10l4-7a2 2 0 0 1 3 2v4h4a2 2 0 0 1 2 2l-1 7a3 3 0 0 1-3 2H7z",
      ),
    ],
    user: [
      circle("head", 12, 8, 3),
      path("body", "M5 20c1.5-4 12.5-4 14 0"),
    ],
    volume: [
      path("speaker", "M4 10h4l5-4v12l-5-4H4z"),
      path("wave-a", "M16 9a4 4 0 0 1 0 6"),
      path("wave-b", "M18 6a8 8 0 0 1 0 12"),
    ],
    "volume-off": [
      path("speaker", "M4 10h4l5-4v12l-5-4H4z"),
      line("slash-a", 17, 9, 21, 15),
      line("slash-b", 21, 9, 17, 15),
    ],
    x: [line("a", 6, 6, 18, 18), line("b", 18, 6, 6, 18)],
  };

  return createElement(
    "svg",
    {
      "aria-hidden": "true",
      focusable: "false",
      height: size,
      style: { display: "block" },
      viewBox: "0 0 24 24",
      width: size,
    },
    ...childrenByName[name],
  );
}
